/**
 * useGeminiVoiceInput.js - Professional Voice Recording Hook
 * 
 * Uses Gemini Speech-to-Text API for high-quality transcription.
 * Features:
 * - Continuous recording (no auto-stop on silence)
 * - Click-to-start, click-to-stop flow
 * - 90 second max recording time
 * - Real-time audio waveform visualization
 * - Supports English and Spanish
 */

import { useState, useRef, useCallback, useEffect } from "react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export function useGeminiVoiceInput(language = "en") {
    const [isRecording, setIsRecording] = useState(false);
    const [isTranscribing, setIsTranscribing] = useState(false);
    const [transcript, setTranscript] = useState("");
    const [error, setError] = useState(null);
    const [isSupported, setIsSupported] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const [audioLevel, setAudioLevel] = useState(0); // 0-100 for waveform
    const [waveformData, setWaveformData] = useState([]); // Array of audio levels for visualization
    
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const streamRef = useRef(null);
    const timerRef = useRef(null);
    const analyserRef = useRef(null);
    const animationFrameRef = useRef(null);
    
    const MAX_RECORDING_TIME = 90; // 90 seconds
    const WAVEFORM_BARS = 20; // Number of bars in waveform

    // Check browser support
    useEffect(() => {
        const supported = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
        setIsSupported(supported);
        if (!supported) {
            console.warn("[Voice Input] getUserMedia not supported in this browser");
        }
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            stopRecording();
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, []);

    // Audio level analysis for waveform
    const startAudioAnalysis = useCallback((stream) => {
        try {
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const analyser = audioContext.createAnalyser();
            const source = audioContext.createMediaStreamSource(stream);
            
            analyser.fftSize = 256;
            analyser.smoothingTimeConstant = 0.85;
            source.connect(analyser);
            
            analyserRef.current = { audioContext, analyser };
            
            const dataArray = new Uint8Array(analyser.frequencyBinCount);
            let lastUpdateTime = 0;
            const UPDATE_INTERVAL = 66; // ~15fps for smoother performance
            
            const updateWaveform = (timestamp) => {
                if (!analyserRef.current) return;
                
                // Throttle updates to reduce render load
                if (timestamp - lastUpdateTime < UPDATE_INTERVAL) {
                    animationFrameRef.current = requestAnimationFrame(updateWaveform);
                    return;
                }
                lastUpdateTime = timestamp;
                
                analyser.getByteFrequencyData(dataArray);
                
                // Calculate average level
                const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
                const normalizedLevel = Math.min(100, (average / 128) * 100);
                setAudioLevel(normalizedLevel);
                
                // Create waveform bars from frequency data
                const step = Math.floor(dataArray.length / WAVEFORM_BARS);
                const bars = [];
                for (let i = 0; i < WAVEFORM_BARS; i++) {
                    const value = dataArray[i * step];
                    bars.push(Math.min(100, (value / 255) * 100));
                }
                setWaveformData(bars);
                
                animationFrameRef.current = requestAnimationFrame(updateWaveform);
            };
            
            animationFrameRef.current = requestAnimationFrame(updateWaveform);
        } catch (err) {
            console.warn("[Voice Input] Audio analysis not available:", err);
        }
    }, []);

    const stopAudioAnalysis = useCallback(() => {
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }
        if (analyserRef.current) {
            try {
                analyserRef.current.audioContext.close();
            } catch (e) {
                // Ignore
            }
            analyserRef.current = null;
        }
        setAudioLevel(0);
        setWaveformData([]);
    }, []);

    // Auto-stop at max recording time
    useEffect(() => {
        if (recordingTime >= MAX_RECORDING_TIME && isRecording) {
            console.log("[Voice Input] Max recording time reached, stopping...");
            stopRecording();
        }
    }, [recordingTime, isRecording]);

    const startRecording = useCallback(async () => {
        if (isRecording || isTranscribing) return;
        
        setError(null);
        setTranscript("");
        audioChunksRef.current = [];
        
        try {
            // Request microphone access
            const stream = await navigator.mediaDevices.getUserMedia({ 
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    sampleRate: 44100
                }
            });
            
            streamRef.current = stream;
            
            // Start audio analysis for waveform visualization
            startAudioAnalysis(stream);
            
            // Determine best supported MIME type
            let mimeType = "audio/webm;codecs=opus";
            if (!MediaRecorder.isTypeSupported(mimeType)) {
                mimeType = "audio/webm";
            }
            if (!MediaRecorder.isTypeSupported(mimeType)) {
                mimeType = "audio/mp4";
            }
            if (!MediaRecorder.isTypeSupported(mimeType)) {
                mimeType = ""; // Let browser choose
            }
            
            const options = mimeType ? { mimeType } : {};
            const mediaRecorder = new MediaRecorder(stream, options);
            
            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };
            
            mediaRecorder.onstop = async () => {
                // Stop audio analysis
                stopAudioAnalysis();
                
                // Stop all tracks
                if (streamRef.current) {
                    streamRef.current.getTracks().forEach(track => track.stop());
                    streamRef.current = null;
                }
                
                // Stop timer
                if (timerRef.current) {
                    clearInterval(timerRef.current);
                    timerRef.current = null;
                }
                
                setIsRecording(false);
                setRecordingTime(0);
                
                // Process audio if we have data
                if (audioChunksRef.current.length > 0) {
                    await sendToTranscription();
                }
            };
            
            mediaRecorderRef.current = mediaRecorder;
            mediaRecorder.start(1000); // Collect data every second
            
            setIsRecording(true);
            
            // Start timer
            timerRef.current = setInterval(() => {
                setRecordingTime(prev => prev + 1);
            }, 1000);
            
            const langDisplay = language.startsWith("es") ? "🌙 Spanish" : "⭐ English";
            console.log(`[Voice Input] Recording started - ${langDisplay}`);
            
        } catch (err) {
            console.error("[Voice Input] Failed to start recording:", err);
            
            if (err.name === "NotAllowedError") {
                setError("Microphone access denied. Please allow microphone access.");
            } else if (err.name === "NotFoundError") {
                setError("No microphone found. Please connect a microphone.");
            } else {
                setError("Failed to start recording. Please try again.");
            }
            
            setIsRecording(false);
        }
    }, [isRecording, isTranscribing, language]);

    const stopRecording = useCallback(() => {
        if (!isRecording || !mediaRecorderRef.current) return;
        
        try {
            if (mediaRecorderRef.current.state !== "inactive") {
                mediaRecorderRef.current.stop();
            }
        } catch (err) {
            console.error("[Voice Input] Error stopping recorder:", err);
            setIsRecording(false);
        }
    }, [isRecording]);

    const sendToTranscription = async () => {
        if (audioChunksRef.current.length === 0) {
            setError("No audio recorded");
            return;
        }
        
        setIsTranscribing(true);
        
        try {
            // Create blob from recorded chunks
            const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
            console.log(`[Voice Input] Sending ${audioBlob.size} bytes for transcription`);
            
            // Create form data
            const formData = new FormData();
            formData.append("audio", audioBlob, "recording.webm");
            formData.append("language", language.startsWith("es") ? "es" : "en");
            
            // Send to backend
            const response = await fetch(`${API_URL}/api/transcribe`, {
                method: "POST",
                body: formData,
                credentials: "include"
            });
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || `HTTP ${response.status}`);
            }
            
            const data = await response.json();
            
            if (data.success) {
                setTranscript(data.transcript);
                console.log(`[Voice Input] Transcription successful: "${data.transcript.substring(0, 50)}..."`);
            } else {
                setError(data.error || "Transcription failed");
            }
            
        } catch (err) {
            console.error("[Voice Input] Transcription error:", err);
            setError(err.message || "Failed to transcribe audio");
        } finally {
            setIsTranscribing(false);
            audioChunksRef.current = [];
        }
    };

    const toggleRecording = useCallback(() => {
        if (isRecording) {
            stopRecording();
        } else {
            startRecording();
        }
    }, [isRecording, startRecording, stopRecording]);

    const clearTranscript = useCallback(() => {
        setTranscript("");
        setError(null);
    }, []);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, "0")}`;
    };

    return {
        // State
        isRecording,
        isTranscribing,
        transcript,
        error,
        isSupported,
        recordingTime,
        maxRecordingTime: MAX_RECORDING_TIME,
        
        // Waveform visualization
        audioLevel,
        waveformData,
        
        // Actions
        startRecording,
        stopRecording,
        toggleRecording,
        clearTranscript,
        
        // Helpers
        formatTime,
        // Celestial language indicators (neutral, culturally inclusive)
        languageDisplay: language.startsWith("es") ? "🌙 ES" : "⭐ EN",
        languageIcon: language.startsWith("es") ? "🌙" : "⭐"
    };
}

export default useGeminiVoiceInput;
